import { styled } from "@linaria/react";
import cx from "classnames";
import React, { useState } from "react";
import { color, radius, fonts } from "@ui";
import Collapse from "@ui/Collapse";
import Link from "@ui/Link";
import Ellipsis from "@ui/Ellipsis";
import InlineMenu from "@ui/InlineMenu";
import Option from "@ui/Option";
import useFilteredDatas from "@ui/lib/hooks/useFilteredDatas";
import { literalUnion, nullable } from "diagnostics-lib/io";
import { comparatorBy } from "diagnostics-lib/comparator";
import { groupBy } from "diagnostics-lib/iterable";
import { StyledScrollbarDiv } from "@ui/StyledScrollbarDiv";
import t from "../../../i18n";
import Modal from "@ui/Modal";

import type { EnhancedQuantity } from "../Settings/Quantities/utils/quantityFilters";
import {
  DEFAULT_QUANTITY_FILTER_VALUES,
  filterQuantities,
  getQuantitiesFilterMetadata,
} from "../Settings/Quantities/utils/quantityFilters";
import FilterSelects, { NONE_LABEL } from "@ui/Filter";
import { quantityRoles, useQuantities, type Quantity } from "../../../api/useQuantities";

import { getQuantityTypeLabel } from "../Settings/Quantities/utils/getQuantityType";
import classNames from "classnames";
import formatNumber from "@ui/i18n/formatNumber";
import AlertMessage from "./AlertMessage";
import { QuantityGroupCard } from "./QuantityIdentityCard";
import AlertBadge from "./AlertBadge";
import Tooltip from "./Tooltip";
import { type Period } from "@ui/Highcharts/hooks/usePeriod";
import { useCanUserDoOnProject } from "../../canUserDo";
import { useSetups } from "../../../api/useSetups";
import { useDateRange } from "../Analysis/useSelectedAnalyses";
import { getAnalysisSetup } from "diagnostics-domain/setup/getSetup";
import { type SelectedQuantities } from "../useSelectedQuantities";
import type { ActiveAnalysis } from "../../../api/useActiveAnalysesStream";
import SearchInputV2 from "@ui/Input/SearchInputV2";
import { AddCircleLine, CloseLine, Filter3Fill, ShareBoxLine } from "@ui/IconV2/react";
import ButtonIconV2, { ButtonIconMarker } from "@ui/ButtonIconV2";
import BlankStateV2 from "@ui/BlankStateV2";
import { useSettings } from "../../../api/useSettings";
import { useMetrologyOverrides } from "../../../api/useMetrologyOverrides";
import type { MetrologyCrossFilters } from "../../../api/useMetrologyCrossFilters";
import { useMetrologyCrossFilters } from "../../../api/useMetrologyCrossFilters";
import { useSearchQuantitiesInput } from "./hook/useSearchQuantitiesInput";
import { useQuantitySelectorFilteredValues } from "./hook/useQuantitySelectorFilteredValues";
import { useShowOnlyAlertQuantities } from "./hook/useShowOnlyAlertQuantities";
import * as io from "io-ts";
import createGlobalState from "@ui/lib/createGlobalState";
import { useActivationFilters, type ActivationFilter } from "../../../api/useActivationFilters";
import { css } from "@linaria/core";
import NoData from "@ui/Illustration/react/NoData";
import { useQuantityIndex } from "../../../api/useQuantities";
import { useQuantityAlerts } from "./useQuantityAlerts";
import { toPath } from "../../../router/helpers/path";
import Switch from "@ui/Switch";
import Button from "@ui/Button";
import { colors, spacings } from "@ui/Theme";
import Drawer from "@ui/Drawer";
import CreateFormulaQuantity, {
  type CreateFormulaQuantityHandle,
} from "../Settings/Quantities/CreateFormulaQuantity";
import Text from "@ui/Text";
import { submit } from "../Settings/Quantities/CreateFormulaQuantity";
import type { EditQuantityHandle } from "../Settings/Quantities/EditQuantity/EditQuantity";
import EditQuantity from "../Settings/Quantities/EditQuantity/EditQuantity";
import { toBasenamePath } from "../../../router/helpers/navigation";
import useModal from "@ui/lib/hooks/useModal";
import useToasts from "@ui/Toast/useToasts";
import { useDelete } from "../../../api/useCall";
import ConfirmDelete from "../Settings/Quantities/modalForm/ConfirmDelete";
import SelectableQuantitiesGroup from "./SelectableQuantitiesGroup";
import { useQuantitySelectorShowDescriptions } from "./hook/useQuantitySelectorShowDescriptions";
import { useShowIntermediateCalculations } from "./hook/useShowIntermediateCalculations";
import { ChartMenu } from "../Chart/Menu";
import TooltipV2 from "@ui/TooltipV2";
import NavV2 from "@ui/NavV2";
import Tab from "@ui/NavV2/Tab";
import SelectedQuantitiesSection from "../SelectedQuantitiesSection";

export enum QuantitySelectorType {
  Measures = "measures",
  Symptoms = "symptoms",
  Diagnosis = "diagnosis",
}

const GROUPING_TYPES = ["SYSTEM", "NATURAL", "UNIT"] as const;
const GROUPING_TYPE_LIST = [...GROUPING_TYPES, null] as const;

const GroupingType = literalUnion(GROUPING_TYPES);
type GroupingType = (typeof GROUPING_TYPES)[number];
const OpenGroupsByGroupingTypeState = io.record(GroupingType, io.array(io.string));

const useQuantitySelectorOpenedGroups = createGlobalState({
  storage: "session",
  key: "quantitySelectorOpenedGroups",
  type: OpenGroupsByGroupingTypeState,
  defaultValue: { SYSTEM: [], UNIT: [], NATURAL: [] },
  scoped: true,
});

const useQuantitySelectorGroupingType = createGlobalState({
  storage: "session",
  key: "quantitySelectorGroupingType",
  type: nullable(GroupingType),
  scoped: true,
});

function getGroupingTitle(groupingType: GroupingType | null) {
  switch (groupingType) {
    case "SYSTEM":
      return t("measuresSymptoms.sectionType.system");
    case "UNIT":
      return t("measuresSymptoms.sectionType.unit");
    case "NATURAL":
      return t("measuresSymptoms.sectionType.type");
    case null:
      return t("measuresSymptoms.sectionType.noGrouping");
    default:
      throw new Error(groupingType satisfies never);
  }
}

const getQuantityGroupsByGroupingType: {
  [k in GroupingType]: (q: Quantity) => string[];
} = {
  SYSTEM: (q: Quantity) => [q.systemId ?? NONE_LABEL],
  UNIT: (q: Quantity) => [q.unit ?? NONE_LABEL],
  NATURAL: (q: Quantity) => [q.type],
};

const getQuantityGroupTitleByGroupingType: {
  [k in GroupingType]: (q: string) => string;
} = {
  SYSTEM: (_: string) => {
    throw new Error("it should not happen");
  },
  UNIT: (s: string | null) => s ?? NONE_LABEL,
  NATURAL: (s: string) => getQuantityTypeLabel(s as Quantity["type"]),
};

const getSystemTitle = (q: Pick<Quantity, "systemId"> | undefined) =>
  q?.systemId ?? t("measuresSymptoms.collapse.noGroup");

const useShowQuantityFilters = createGlobalState({
  storage: "session",
  key: "showQuantityFilters",
  type: io.boolean,
  defaultValue: false,
  scoped: true,
});

interface P {
  projectId: string;
  selectedQuantities: SelectedQuantities;
  setSelectedQuantities: (newQuantities: Quantity[]) => void;
  onlyFirstDisplayed: boolean;
  activeAnalyses: ActiveAnalysis[];
  selectedAnalyses: ActiveAnalysis[];
  alertType: "measures" | "symptoms";
  page: QuantitySelectorType;
  period: Period;
  allAnalysesMode: boolean;
  timezone: string;
}

const QuantitySelector = ({
  timezone,
  projectId,
  selectedQuantities,
  setSelectedQuantities,
  onlyFirstDisplayed,
  activeAnalyses,
  selectedAnalyses,
  alertType,
  page,
  period,
  allAnalysesMode,
}: P) => {
  const [dateRange] = useDateRange({
    scope: projectId,
  });
  const setups = useSetups(projectId);
  const setup =
    dateRange.pivot === null ? null : getAnalysisSetup(setups, { date: dateRange.pivot });

  const crossFilters: MetrologyCrossFilters = useMetrologyCrossFilters(projectId);
  const quantityIndex = useQuantityIndex(projectId);

  const metrologyOverrides = useMetrologyOverrides(projectId);
  const quantities = useQuantities(projectId);
  const canUserViewIntermediateCalculations = useCanUserDoOnProject("PROJECT_SETUP", projectId);
  const [showIntermediateCalculations, setShowIntermediateCalculations] =
    useShowIntermediateCalculations({
      scope: `${projectId}:${page}`,
    });
  const visibleQuantities = React.useMemo(
    () =>
      quantities.filter(
        quantity =>
          !quantity.hidden || (canUserViewIntermediateCalculations && showIntermediateCalculations),
      ),
    [canUserViewIntermediateCalculations, quantities, showIntermediateCalculations],
  );
  React.useEffect(() => {
    const visibleQuantityIds = new Set(visibleQuantities.map(quantity => quantity.id));
    const selectedVisibleQuantities = selectedQuantities
      .filter(({ quantity }) => visibleQuantityIds.has(quantity.id))
      .map(({ quantity }) => quantity);

    if (selectedVisibleQuantities.length === selectedQuantities.length) return;

    const nextSelectedQuantities = selectedVisibleQuantities[0]
      ? selectedVisibleQuantities
      : visibleQuantities[0]
        ? [visibleQuantities[0]]
        : [];

    if (nextSelectedQuantities.length > 0) setSelectedQuantities(nextSelectedQuantities);
  }, [selectedQuantities, setSelectedQuantities, visibleQuantities]);

  const [showQuantityFilters, setShowQuantityFilters] = useShowQuantityFilters({
    scope: projectId,
  });
  const toggleShowQuantityFilters = React.useCallback(() => {
    setShowQuantityFilters(value => !value);
  }, [setShowQuantityFilters]);

  const [quantityToModify, setQuantityToModify] = React.useState<Quantity | null>(null);

  const createQuantityRef = React.useRef<CreateFormulaQuantityHandle>(null);
  const [isCreateDrawerOpen, setIsCreateDrawerOpen] = React.useState(false);
  const [createQuantityName, setCreateQuantityName] = React.useState<string | null>(null);

  const handleCloseCreateDrawer = React.useCallback(() => {
    createQuantityRef.current?.resetForm();
    setIsCreateDrawerOpen(false);
    setCreateQuantityName(null);
  }, [createQuantityRef]);

  const editQuantityRef = React.useRef<EditQuantityHandle>(null);
  const [isEditDrawerOpen, setIsEditDrawerOpen] = React.useState(false);
  const [quantityName, setQuantityName] = React.useState<string | null>(null);
  const isDrawerOpen = isEditDrawerOpen || isCreateDrawerOpen;

  const { closeModal, openModal, modal: isDeleteModalOpen } = useModal();
  const { addToast } = useToasts();
  const [tab, setTab] = useState<"general-information" | "formulae">("general-information");
  const handleEditQuantity = React.useCallback((q: Quantity, focusFormulaeView?: boolean) => {
    setQuantityToModify(q);
    setQuantityName(q.name);
    setTab(focusFormulaeView ? "formulae" : "general-information");
    setIsEditDrawerOpen(true);
  }, []);

  const handleDeleteQuantity = (q: Quantity) => {
    setQuantityToModify(q);
    openModal();
  };

  const deleteQuantity = useDelete("/projects/:projectId(uuid)/quantities/:quantityId(uuid)");

  const handleDelete = async () => {
    if (!quantityToModify) throw new Error("Should not happen");

    try {
      await deleteQuantity({
        routeParams: { projectId, quantityId: quantityToModify.id },
      });
      addToast("success", t("project.quantity.toast.deleted.success"));
    } catch (error) {
      addToast("error", t("errors.backend_error"));
    }

    return true;
  };

  const handleCloseEditDrawer = React.useCallback(() => {
    setIsEditDrawerOpen(false);
    setQuantityToModify(null);
    setQuantityName(null);
  }, []);

  const quantityAlerts = useQuantityAlerts({
    activeAnalyses,
    metrologyOverrides,
    quantityIndex,
    setups,
    period,
    selectedAnalyses,
    alertType,
  });

  const enhancedQuantities: EnhancedQuantity[] = React.useMemo(
    () =>
      visibleQuantities.sort(comparatorBy("name")).map(q => ({
        ...q,
        roles: quantityRoles(q, setup ? [setup] : []),
        isPromoted: false,
        filters: [
          typeof q.minMetrology === "number" ||
          typeof q.maxMetrology === "number" ||
          typeof q.maxStdMetrology === "number" ||
          crossFilters.some(c => c.invalidatedQuantityName === q.name)
            ? "metrology"
            : null,
          typeof q.minActivation === "number" ||
          typeof q.maxActivation === "number" ||
          typeof q.maxStdActivation === "number"
            ? "activation"
            : null,
        ].filter((f): f is NonNullable<"activation" | "metrology"> => f !== null),
      })),
    [crossFilters, visibleQuantities, setup],
  );

  const settings = useSettings();
  const [groupingType, setGroupingType] = useQuantitySelectorGroupingType({
    scope: projectId,
    defaultValue: settings.PROJECT_TYPE_QUANTITY_GROUP ? "SYSTEM" : null,
  });

  const [search, setSearch] = useSearchQuantitiesInput({ scope: projectId });
  const [openGroups, setOpenGroups] = useQuantitySelectorOpenedGroups({
    scope: projectId,
  });
  const [showQuantityDescriptions, setShowQuantityDescriptions] =
    useQuantitySelectorShowDescriptions({
      scope: projectId,
    });
  const [showOnlyAlertQuantities, setShowOnlyAlertQuantities] = useShowOnlyAlertQuantities({
    scope: `${projectId}:${page}`,
  });

  // Invalid quantities (measures) or symptomatic quantities (symptoms)
  const alertQuantityIds = React.useMemo(
    () =>
      new Set(
        Object.entries(quantityAlerts)
          .filter(qa => qa[1].isInAlert)
          .map(qa => qa[0]),
      ),
    [quantityAlerts],
  );

  const rawAlertQuantities = React.useMemo(
    () => enhancedQuantities.filter(quantity => alertQuantityIds.has(quantity.id)),
    [enhancedQuantities, alertQuantityIds],
  );

  const alertQuantities = alertQuantityIds.size === 0 ? [] : rawAlertQuantities;

  const sortedQuantities = showOnlyAlertQuantities ? alertQuantities : enhancedQuantities;

  const [filteredValues, setFilteredValues] = useQuantitySelectorFilteredValues({
    scope: projectId,
  });

  const hasActiveQuantityFilters = Object.values(filteredValues).some(arr => arr.length > 0);

  const filteredQuantities = React.useMemo(
    () => filterQuantities(sortedQuantities, filteredValues),
    [sortedQuantities, filteredValues],
  );
  const [searchedQuantities] = useFilteredDatas(
    filteredQuantities,
    search,
    quantity => `${quantity.name} ${quantity.description}`,
  );

  const groups = React.useMemo(() => {
    if (!groupingType) return [];
    const getQuantityGroups = getQuantityGroupsByGroupingType[groupingType];
    const quantityGroupIndex = groupBy(searchedQuantities, getQuantityGroups);
    const getGroupTitle = getQuantityGroupTitleByGroupingType[groupingType];

    return Array.from(quantityGroupIndex.entries())
      .map(([groupValue, groupQuantities]) => ({
        id: groupValue,
        title:
          groupingType === "SYSTEM" && groupQuantities !== undefined
            ? getSystemTitle(groupQuantities[0])
            : getGroupTitle(groupValue),
        quantities: groupQuantities,
      }))
      .sort((g1, g2) => {
        if (g1.id === NONE_LABEL) return g2.id === NONE_LABEL ? 0 : 1;
        if (g2.id === NONE_LABEL) return -1;

        return g1.title.localeCompare(g2.title);
      });
  }, [searchedQuantities, groupingType]);

  const canUserEditQuantities = useCanUserDoOnProject("PROJECT_WRITE", projectId);

  const showRolesFilter = enhancedQuantities.some(quantity => !quantity.roles.has("others"));
  const hideUnitsFilter = enhancedQuantities.every(quantity => quantity.unit === null);

  const handleSearchChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearch(e.currentTarget.value);
    },
    [setSearch],
  );

  const activationFilters = useActivationFilters(projectId);
  const activationFiltersByQuantityId = React.useMemo(() => {
    const activationFiltersByQuantityId: {
      [quantityId: string]: ActivationFilter;
    } = {};
    for (const af of activationFilters) {
      activationFiltersByQuantityId[af.quantityId] = af;
    }
    return activationFiltersByQuantityId;
  }, [activationFilters]);

  const crossFiltersByQuantityName = React.useMemo(() => {
    const crossFiltersByQuantityName: { [quantityId: string]: MetrologyCrossFilters } = {};
    for (const cf of crossFilters) {
      if (!crossFiltersByQuantityName[cf.invalidatedQuantityName]) {
        crossFiltersByQuantityName[cf.invalidatedQuantityName] = [];
      }
      crossFiltersByQuantityName[cf.invalidatedQuantityName]!.push(cf);
    }
    return crossFiltersByQuantityName;
  }, [crossFilters]);

  const observableIdsBySetupId = React.useMemo(() => {
    const observableIdsBySetupId: { [setupId: string]: Set<string> } = {};
    for (const setup of setups) {
      for (const observable of setup.diagnosisInputs) {
        if (observable.enabled) {
          const observableIds = observableIdsBySetupId[setup.id];
          if (observableIds) {
            observableIds.add(observable.quantityId);
          } else {
            observableIdsBySetupId[setup.id] = new Set([observable.quantityId]);
          }
        }
      }
    }
    return observableIdsBySetupId;
  }, [setups]);

  return (
    <>
      <div className={wrapper}>
        <div className={header}>
          <div className={titleWrapper}>
            <div className={title}>{t("measuresSymptoms.collapse.title")}</div>
            <SectionTypes
              data-cy="section-types"
              caret
              anchor="right"
              overlay={GROUPING_TYPE_LIST.map(gt => (
                <Option
                  data-cy={gt}
                  key={gt}
                  selected={gt === groupingType}
                  onClick={() => setGroupingType(gt)}
                  closeOnClick
                >
                  {getGroupingTitle(gt)}
                </Option>
              ))}
            >
              <Link>{getGroupingTitle(groupingType)}</Link>
            </SectionTypes>
          </div>
        </div>
        <div className={filtersContainer}>
          <div className={filtersFirstLine}>
            <SearchInputV2
              data-cy="search"
              placeholder={t("project.quantities.search.name_or_description")}
              value={search}
              onChange={handleSearchChange}
              className={searchInput}
            />

            <div className={filterButtonWrapper}>
              <TooltipV2 overlay={t("quantitySelector.filter.tooltip")}>
                <ButtonIconV2
                  onClick={toggleShowQuantityFilters}
                  className={classNames(activeButtonIcon, { active: showQuantityFilters })}
                >
                  {hasActiveQuantityFilters && <ButtonIconMarker />}
                  <Filter3Fill height={24} width={24} />
                </ButtonIconV2>
              </TooltipV2>
              <ChartMenu
                marker={
                  showOnlyAlertQuantities ||
                  showQuantityDescriptions ||
                  (canUserViewIntermediateCalculations && showIntermediateCalculations)
                }
                className={quantitySelectorChartMenu}
                tooltipOverlay={t("quantitySelector.display_options.tooltip")}
              >
                {canUserViewIntermediateCalculations && (
                  <Switch
                    checked={showIntermediateCalculations}
                    onChange={() => setShowIntermediateCalculations(value => !value)}
                    position="left"
                  >
                    {t("quantitySelector.toggle.showIntermediateCalculations")}
                  </Switch>
                )}
                <Switch
                  checked={showOnlyAlertQuantities}
                  onChange={() => setShowOnlyAlertQuantities(!showOnlyAlertQuantities)}
                  position="left"
                >
                  {page === QuantitySelectorType.Symptoms
                    ? t("quantitySelector.toggle.symptomatic")
                    : t("quantitySelector.toggle.unavailable")}
                </Switch>
                <Switch
                  checked={showQuantityDescriptions}
                  onChange={() => setShowQuantityDescriptions(!showQuantityDescriptions)}
                  position="left"
                >
                  {t("quantitySelector.toggle.showDescriptions")}
                </Switch>
              </ChartMenu>
            </div>
          </div>

          {showQuantityFilters && (
            <div className={filterWrapper}>
              <FilterSelects
                wrappable
                data={sortedQuantities}
                metadata={getQuantitiesFilterMetadata(showRolesFilter, hideUnitsFilter)}
                filterValues={filteredValues}
                setFilterValues={setFilteredValues}
                defaultFilterValues={DEFAULT_QUANTITY_FILTER_VALUES}
              />
            </div>
          )}
        </div>
        <BodyWrapper>
          <SelectedQuantitiesSection
            projectId={projectId}
            visibleQuantities={visibleQuantities}
            groupingType={groupingType}
            selectedQuantities={selectedQuantities}
            setSelectedQuantities={setSelectedQuantities}
            onlyFirstDisplayed={onlyFirstDisplayed}
            showQuantityDescriptions={showQuantityDescriptions}
            quantityAlerts={quantityAlerts}
            activationFiltersByQuantityId={activationFiltersByQuantityId}
            crossFiltersByQuantityName={crossFiltersByQuantityName}
            allAnalysesMode={allAnalysesMode}
            alertType={alertType}
            handleEditQuantity={handleEditQuantity}
            handleDeleteQuantity={handleDeleteQuantity}
            isDrawerOpen={isDrawerOpen}
          />
          {searchedQuantities.length ? (
            <div className={body} data-cy="body-quantities">
              {groupingType === null ? (
                <SelectableQuantitiesGroup
                  projectId={projectId}
                  quantities={searchedQuantities}
                  selectedQuantities={selectedQuantities}
                  setSelectedQuantities={setSelectedQuantities}
                  onlyFirstDisplayed={onlyFirstDisplayed}
                  showQuantityDescriptions={showQuantityDescriptions}
                  quantityAlerts={quantityAlerts}
                  activationFiltersByQuantityId={activationFiltersByQuantityId}
                  crossFiltersByQuantityName={crossFiltersByQuantityName}
                  allAnalysesMode={allAnalysesMode}
                  alertType={alertType}
                  handleEditQuantity={handleEditQuantity}
                  handleDeleteQuantity={handleDeleteQuantity}
                  isDrawerOpen={isDrawerOpen}
                />
              ) : (
                groups.map(group => {
                  const isGroupOpen = openGroups[groupingType].includes(group.id);
                  const groupQuantities = group.quantities;
                  const setupOberservableIds = setup && observableIdsBySetupId[setup.id];

                  //only observable have symptoms and alerts
                  const observableQuantities = setupOberservableIds
                    ? groupQuantities.filter(q => setupOberservableIds.has(q.id))
                    : [];
                  const hasCurrentAlert = groupQuantities.some(
                    quantity => quantityAlerts[quantity.id]?.isInAlert,
                  );

                  const alertInRange =
                    page === QuantitySelectorType.Symptoms
                      ? observableQuantities[0]
                        ? quantityAlerts[observableQuantities[0].id]!.inRange.map((_, i) =>
                            observableQuantities.map(
                              quantity => quantityAlerts[quantity.id]!.inRange[i],
                            ),
                          )
                        : []
                      : (quantityAlerts[groupQuantities[0]!.id]!.inRange.map((_, i) =>
                          groupQuantities.map(quantity => quantityAlerts[quantity.id]?.inRange[i]),
                        ) ?? []);

                  const alertCount = alertInRange.filter(i => i.some(j => j)).length;
                  const analysisCount = alertInRange.filter(i => i.some(j => j !== null)).length;
                  const alertPercentage = (alertCount / analysisCount) * 100;

                  const sectionTitle = (
                    <div className={sectionTitleCss}>
                      <Ellipsis title={group.title}>{group.title}</Ellipsis>
                      {allAnalysesMode ? null : (
                        <AlertBadge
                          withIcon={hasCurrentAlert}
                          alertPercentage={alertCount && alertPercentage}
                          alertType={alertType}
                        />
                      )}
                    </div>
                  );

                  return (
                    <GroupSection
                      key={group.id}
                      title={
                        alertCount !== 0 || hasCurrentAlert ? (
                          <Tooltip
                            overlay={
                              <QuantityGroupCard
                                name={group.title}
                                alertMessage={
                                  <AlertMessage
                                    alertType={alertType}
                                    currentMessage={
                                      hasCurrentAlert
                                        ? alertType === "measures"
                                          ? t("quantitySelector.group.alert.measures.current")
                                          : t("quantitySelector.group.alert.symptoms.current")
                                        : null
                                    }
                                    message={
                                      alertCount
                                        ? alertType === "measures"
                                          ? t("quantitySelector.group.alert.measures.inRange", {
                                              alertCount,
                                              analysisCount,
                                              alertPercentage: formatNumber(alertPercentage, {
                                                precision: 0,
                                              }),
                                            })
                                          : t("quantitySelector.group.alert.symptoms.inRange", {
                                              alertCount,
                                              analysisCount,
                                              alertPercentage: formatNumber(alertPercentage, {
                                                precision: 0,
                                              }),
                                            })
                                        : null
                                    }
                                  />
                                }
                              />
                            }
                          >
                            {sectionTitle}
                          </Tooltip>
                        ) : (
                          sectionTitle
                        )
                      }
                      onToggle={() =>
                        setOpenGroups({
                          ...openGroups,
                          [groupingType]: isGroupOpen
                            ? openGroups[groupingType].filter(g => g !== group.id)
                            : [...openGroups[groupingType], group.id],
                        })
                      }
                      opened={isGroupOpen}
                    >
                      <SelectableQuantitiesGroup
                        projectId={projectId}
                        quantities={groupQuantities}
                        selectedQuantities={selectedQuantities}
                        setSelectedQuantities={setSelectedQuantities}
                        onlyFirstDisplayed={onlyFirstDisplayed}
                        showQuantityDescriptions={showQuantityDescriptions}
                        quantityAlerts={quantityAlerts}
                        activationFiltersByQuantityId={activationFiltersByQuantityId}
                        crossFiltersByQuantityName={crossFiltersByQuantityName}
                        allAnalysesMode={allAnalysesMode}
                        alertType={alertType}
                        handleEditQuantity={handleEditQuantity}
                        handleDeleteQuantity={handleDeleteQuantity}
                        isDrawerOpen={isDrawerOpen}
                      />
                    </GroupSection>
                  );
                })
              )}
            </div>
          ) : (
            <div className={blankstateOverflow}>
              <BlankStateV2
                title={t("measuresSymptoms.blankstate.no_quantities_found")}
                picto={<NoData size="m" />}
                data-cy="blankstate"
              />
            </div>
          )}
        </BodyWrapper>
        <div className={footerContainer}>
          {canUserEditQuantities && (
            <Button
              leftIcon={<AddCircleLine size="s" />}
              variant="secondary"
              slim
              className={footerActionButton}
              onClick={() => {
                setIsCreateDrawerOpen(true);
              }}
              disabled={isDrawerOpen}
              title={
                isDrawerOpen ? t("quantitySelector.actionDisabledBecauseDrawerOpen") : undefined
              }
            >
              {t("quantitySelector.actions.create")}
            </Button>
          )}
          <Button
            leftIcon={<ShareBoxLine color={color.titanium800} size="s" />}
            variant="tertiary"
            className={footerActionButton}
            slim
            onClick={() =>
              window.open(
                toBasenamePath(toPath("/projects/:projectId/settings/quantities", { projectId })),
              )
            }
          >
            {t("quantitySelector.actions.viewList")}
          </Button>
        </div>
      </div>
      <Drawer
        title={t("project.settings.quantity.side_panel.create.title")}
        isOpen={isCreateDrawerOpen}
        className={noPaddingDrawer}
        onRequestClose={handleCloseCreateDrawer}
        header={
          <div className={headerContainer}>
            <div className={titleBlock}>
              <Text type="h4">{t("project.settings.quantity.side_panel.create.title")}</Text>
              {createQuantityName ? (
                <Text type="body1" className={titleSubtitle}>
                  {createQuantityName}
                </Text>
              ) : null}
            </div>
            <ButtonIconV2 onClick={handleCloseCreateDrawer}>
              <CloseLine size="s" color={colors.carbon800} />
            </ButtonIconV2>
            <NavV2>
              <Tab
                id="general-information"
                className={cx({ active: tab === "general-information" })}
                onClick={() => setTab("general-information")}
              >
                {t("project.settings.quantity.side_panel.tab.general_information")}
              </Tab>
              <Tab
                id="formula"
                className={cx({ active: tab === "formulae" })}
                onClick={() => setTab("formulae")}
              >
                {t("project.settings.quantity.side_panel.tab.formulae")}
              </Tab>
            </NavV2>
          </div>
        }
      >
        <CreateFormulaQuantity
          view={tab}
          ref={createQuantityRef}
          projectId={projectId}
          closePanel={handleCloseCreateDrawer}
          className={quantityPanelWrapper}
          timezone={timezone}
          onNameChange={setCreateQuantityName}
        />
      </Drawer>
      <Drawer
        title={t("project.settings.quantity.side_panel.edit.title")}
        isOpen={isEditDrawerOpen}
        className={noPaddingDrawer}
        onRequestClose={handleCloseEditDrawer}
        header={
          <div className={headerContainer}>
            <div className={titleBlock}>
              <Text type="h4">{t("project.settings.quantity.side_panel.edit.title")}</Text>
              {quantityToModify ? (
                <Text type="body1" className={titleSubtitle}>
                  {quantityName ?? quantityToModify.name}
                </Text>
              ) : null}
            </div>
            <ButtonIconV2 onClick={handleCloseEditDrawer}>
              <CloseLine size="s" color={colors.carbon800} />
            </ButtonIconV2>
            <NavV2>
              <Tab
                id="general-information"
                className={cx({ active: tab === "general-information" })}
                onClick={() => setTab("general-information")}
              >
                {t("project.settings.quantity.side_panel.tab.general_information")}
              </Tab>
              {quantityToModify?.type === "FORMULA" && (
                <Tab
                  id="formula"
                  className={cx({ active: tab === "formulae" })}
                  onClick={() => setTab("formulae")}
                >
                  {t("project.settings.quantity.side_panel.tab.formulae")}
                </Tab>
              )}
            </NavV2>
          </div>
        }
      >
        {isEditDrawerOpen && quantityToModify && (
          <EditQuantity
            timezone={timezone}
            view={tab}
            ref={editQuantityRef}
            projectId={projectId}
            closePanel={handleCloseEditDrawer}
            className={quantityPanelWrapper}
            quantity={quantityToModify}
            onNameChange={setQuantityName}
          />
        )}
      </Drawer>
      {isDeleteModalOpen && quantityToModify && (
        <Modal title={t("project.quantities.delete.title")} isOpen onRequestClose={closeModal}>
          <ConfirmDelete
            projectId={projectId}
            onRequestClose={closeModal}
            quantity={quantityToModify}
            handleSubmit={handleDelete}
          />
        </Modal>
      )}
    </>
  );
};

export default React.memo(QuantitySelector);

//#region CSS
const GroupSection = styled(Collapse)`
  content-visibility: auto;
  & + & {
    margin-top: 20px;
  }
`;

const quantityPanelWrapper = css`
  & .${submit} {
    background-color: ${colors.hydrogen};
  }
`;

const sectionTitleCss = css`
  display: flex;
  gap: 4px;
  color: ${color.carbon900};
  font: ${fonts.body1SemiBold};
  width: 100%;
`;

const wrapper = css`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${spacings.m};
  flex: 1 0 0;
  align-self: stretch;
`;

const BodyWrapper = styled(StyledScrollbarDiv())`
  position: relative;
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding-top: 16px;
  padding-right: 16px;
  border-top: 1px solid;
  border-top-color: ${color.titanium200};
  transform: translate3d(0, 0, 0);
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${spacings.m};
  align-self: stretch;
`;

const blankstateOverflow = css`
  height: 0;
  & > div {
    height: auto;
    padding: 0 20px;
    justify-content: flex-start;
  }
`;

const body = css`
  height: 0; /* do not force wrapper to adapt to content size */
  display: flex;
  flex-direction: column;
  width: 100%;
  margin-right: ${spacings.m};
`;

const header = css`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;
`;

const titleWrapper = css`
  margin-top: 5px;
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  align-items: center;
  align-self: stretch;
`;

const title = css`
  color: ${color.carbon900};
  font: ${fonts.body1SemiBold};
  text-transform: capitalize;
`;

const SectionTypes = styled(InlineMenu)`
  font: ${fonts.body2SemiBold};
  color: ${color.carbon900};
  & > a {
    display: flex;
    justify-content: center;
    align-items: center;
    border-radius: ${radius};
  }
`;

const activeButtonIcon = css`
  &.active {
    color: ${color.lightNitrogen500_Main};
    background-color: ${color.lightNitrogen50};
  }
`;

const filtersContainer = css`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: ${spacings.m};
  align-self: stretch;
  width: 100%;
`;

const filterWrapper = css`
  display: flex;
  align-items: flex-start;
  column-gap: ${spacings.xxs};
`;

const filtersFirstLine = css`
  display: flex;
  align-items: center;
  gap: ${spacings.xs};
  align-self: stretch;
`;

const filterButtonWrapper = css`
  display: flex;
  gap: ${spacings.xs};
  align-items: center;
`;

const quantitySelectorChartMenu = css`
  & button {
    height: 36px;
    width: 36px;
  }
`;

const searchInput = css`
  flex-grow: 1;
  min-width: 0;
  margin: 0;
`;

const footerContainer = css`
  display: flex;
  align-items: flex-start;
  gap: ${spacings.m};
  align-self: stretch;
  position: static;
  border-top: 1px solid ${color.titanium200};
  padding-top: ${spacings.m};
  z-index: 1;
`;

const footerActionButton = css`
  padding: ${spacings.xs};
  flex: 1 0 0;
  gap: ${spacings.xxs};
`;

const noPaddingDrawer = css`
  .drawer-wrapper {
    padding: 0;
    overflow-y: auto;
    overscroll-behavior: contain;
  }
`;

const headerContainer = css`
  z-index: 2;
  position: sticky;
  top: 0;

  display: grid;
  grid-template-columns: 1fr max-content;
  grid-template-rows: repeat(2, max-content);

  background-color: ${colors.hydrogen};

  & > *:nth-child(1),
  & > *:nth-child(2) {
    align-self: center;
    margin: 16px;
  }

  nav {
    border-bottom: 1px solid ${colors.titanium100};
    grid-column: 1 / -1;
    grid-row: 2/3;
  }
`;

const titleBlock = css`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: ${spacings.xxs};
`;

const titleSubtitle = css`
  min-width: 0;
  overflow-wrap: anywhere;
`;
//#endregion CSS
